<template>
<div class="chapter">
<div class="inner_page">
      ﻿



<title>The Whole Church Sings</title>
<link href="../styles/stylesheet.css" rel="stylesheet" type="text/css"/>
<p class="CN" id="ch002"><a id="page_8"></a><a href="#/xhtml/contents#ch002">2</a></p>
<p class="CT"><a href="#/xhtml/contents#ch002"><i>Pre-Reformation Vernacular Song</i></a></p>
<p class="Bodyflush"><span class="dropcap">I</span>t is difficult for people of the twenty-first century to fully comprehend the extent and importance of vernacular song in earlier centuries. No aspect of life, serious or frivolous, no detail of news, national or local, no human emotion, euphoric or dysphoric, no natural event, phenomenal or catastrophic, would be allowed to pass without becoming the subject of vernacular songs. The music of today’s popular culture, conveyed individually to our ears through the electronic ether by various technological toys—the apex of the vigorous progress of recorded sound over the previous century—has effectively eliminated this long tradition of vernacular folk song, though parts of it were kept alive well into the twentieth century in the songs of protest and social concern by Pete Seeger, Joan Baez, Bob Dylan, to name just a few, and other aspects morphed into elements of what is known as Country Music. Of course, we sing along with our MP3 players to the latest popular songs that express human aspirations and emotions. But the transmission is top-down and immediate, the opposite of the bottom-up communication of the older folk-song tradition, in which melodies were slowly honed and modified<sup><a href="#/xhtml/#rfn1" id="fn1">1</a></sup> and texts were developed and expanded<sup><a href="#/xhtml/#rfn2" id="fn2">2</a></sup> by oral transmission from person to person and group to group.</p>
<p class="H1"><a id="page_9"><i>V<span class="small">OLKSLIEDER</span></i></a></p><a id="page_9">
</a><p class="Bodyflush1"><a id="page_9">Between the end of the nineteenth century and the early twentieth century, there was significant interest in folk song, and collectors—such as Frank Kidson, Sabine Baring-Gould, Lucy Broadwood, Cecil Sharp, Ralph Vaughan Williams, and others in the English-speaking world—sought to preserve these songs, many of which had been orally transmitted for generations.<sup></sup></a><a href="#/xhtml/#rfn3" id="fn3">3</a> But at the time the songs were being harvested, the folk-song tradition, as with much of rural culture, was passing away.<sup><a href="#/xhtml/#rfn4" id="fn4">4</a></sup></p>
<p class="Body">The collecting and study of folk songs was not confined to the British Isles or to the English language.<sup><a href="#/xhtml/#rfn5" id="fn5">5</a></sup> Similarly, while some of the collectors transcribed orally transmitted songs that were then still being sung, others searched for manuscript and printed sources that witnessed to vernacular songs that reach back into the medieval period, songs that are frequently religious in content, sometimes with macaronic texts, that is, a mixture of Latin with the vernacular.<sup><a href="#/xhtml/#rfn6" id="fn6">6</a></sup> In German-speaking areas the primary collections are those of August Hoffmann von Fallersleben, Rochus von <a id="page_10">Liliencron, and Franz Böhme,<sup></sup></a><a href="#/xhtml/#rfn7" id="fn7">7</a> and before them, the anthology of Johann Gottfried Herder.<sup><a href="#/xhtml/#rfn8" id="fn8">8</a></sup></p>
<p class="Body">Among Herder’s primary sources of Volkslieder is a small octavo volume that, after his death in 1803, was purchased, from the auction of his library, by the Herzogin Anna Amalia Bibliothek, Weimar.<sup><a href="#/xhtml/#rfn9" id="fn9">9</a></sup> Bound together in this single volume are no less than sixty-nine different imprints of small pamphlets originally published between ca. 1515 and ca. 1550. Apart from the first item, which comprises some eighty pages with 58 folk songs, most imprints run to no more than eight or sixteen pages,<sup><a href="#/xhtml/#rfn10" id="fn10">10</a></sup> each one usually comprising 1–3 folk songs. Together these imprints contain nearly 200 folk songs (including duplicates), a rich resource of ephemeral literature that generally has not survived unless preserved by being bound into such composite volumes, which are in themselves rarities.<sup><a href="#/xhtml/#rfn11" id="fn11">11</a></sup></p>
<p class="Body">A significant factor concerning the imprints in this volume is that all of them appear to have been printed in Nuremberg, a noted center for vernacular song and poetry.<sup><a href="#/xhtml/#rfn12" id="fn12">12</a></sup> Sixty-one titles were definitely issued by Nuremberg printers, <a id="page_11">since their names are appended to their publications: fifteen by Hans Guldenmund, fifteen by Jobst Gutknecht, and thirty-one by Kunegunde Hergot. The remaining imprints, which have no printer’s colophon, almost certainly were also printed in Nuremberg. For example, the first item, <i>Bergkreyen. Etliche Schöne gesenge</i> (Miners’ melodies [lit.: mountain dances]. Some beautiful songs),<sup></sup></a><a href="#/xhtml/#rfn13" id="fn13">13</a> can be identified as coming from the combined Nuremberg printing house of Georg Wachter and Kunegunde Hergot.<sup><a href="#/xhtml/#rfn14" id="fn14">14</a></sup> From comparison with copies in other libraries it can be ascertained that the anonymous imprints <i>Ein new Lied, Von aim Schneyder und Schumacher</i> (A new song of a taylor and shoemaker)<sup><a href="#/xhtml/#rfn15" id="fn15">15</a></sup> and <i>Ein neues Lied von einer Fischerin</i> (A new song of a fish-wife)<sup><a href="#/xhtml/#rfn16" id="fn16">16</a></sup> were printed in Nuremberg by Kunegunde Hergot. Since some of the content of the final item in Herder’s volume, published anonymously as <i>Ein geystliche Lied/ Ich danck dir lieber Herr . . .</i> (A spiritual song, I thank thee, dear Lord),<sup><a href="#/xhtml/#rfn17" id="fn17">17</a></sup> is also found in an eight-page pamphlet issued in 1535 by Wachter,<sup><a href="#/xhtml/#rfn18" id="fn18">18</a></sup> it too likely originated in Nuremberg. Similarly, while most of the songs are anonymous, a significant proportion of the few named poets are known to have worked in Nuremberg: for example, Hans Folz (ca. 1450–1515),<sup><a href="#/xhtml/#rfn19" id="fn19">19</a></sup> Jörg Graff (ca. 1473/80–1542),<sup><a href="#/xhtml/#rfn20" id="fn20">20</a></sup> Lienhart Nunnenpeck (d. after 1513),<sup><a href="#/xhtml/#rfn21" id="fn21">21</a></sup> and Nunnenpeck’s famous pupil, the astonishingly prolific Hans Sachs (1494–1576).<sup><a href="#/xhtml/#rfn22" id="fn22">22</a></sup></p>
<p class="Body">Most of the undated imprints appear to have been issued from the later <a id="page_12">1520s. Those with Kunegunde Hergot’s colophon have to be dated after 1527, the year she inherited her husband’s printing firm after his execution for heresy. However, the contents of all these pamphlets include songs that date from much earlier periods, as the following examples demonstrate. The anonymous imprint, but almost certainly stemming from Wachter’s printing shop in 1530, <i>Der segen des starcken Poppen</i> (The blessing of strong Poppen),<sup></sup></a><a href="#/xhtml/#rfn23" id="fn23">23</a> has a similar title and the same woodcut as the pamphlet issued by the Nuremberg printer Wolfgang Huber in 1510.<sup><a href="#/xhtml/#rfn24" id="fn24">24</a></sup> The linked songs, beginning <i>Klopff an</i>, in the Kunegunde Hergot imprint <i>Fast abentheürlich Klopf an</i> (Upon adventurous knocking) was written by Hans Folz, who died in 1513.<sup><a href="#/xhtml/#rfn25" id="fn25">25</a></sup> Similarly, the last pamphlet, <i>Ein geystliche Lied/ Ich danck dir lieber Herr . . .</i> (referred to above), includes Luther’s morning prayer from his Small Catechism, which means that the imprint has to be dated after 1529, but it also includes the song <i>Hilf Gott das mir gelinge</i>,<sup><a href="#/xhtml/#rfn26" id="fn26">26</a></sup> written by Heinrich von Zutphen, a former student of Luther in Wittenberg who was executed in 1524 for preaching heresy.<sup><a href="#/xhtml/#rfn27" id="fn27">27</a></sup></p>
<p class="Body">None of the Nuremberg imprints in <i>Das Weimarer Liederbuch</i> includes musical notation. The development of music printing was still in its infancy, and the technological problem of printing individual notes on and between the different lines of the stave had not been fully worked out. In the early sixteenth century, music was printed from woodcuts, a process that demanded time and skill to create, with an end result that was often somewhat crude. But the process was still being used in the first half of the sixteenth century; for example, the music of Luther’s <i>Deutsche Messe</i> (1526) was produced from woodcuts.<sup><a href="#/xhtml/#rfn28" id="fn28">28</a></sup> Although Petrucci in Venice had pioneered the printing of music from movable type in 1501, it took twenty years or more for printers and publishers elsewhere to begin to follow suit.<sup><a href="#/xhtml/#rfn29" id="fn29">29</a></sup> Thus in theory the Nuremberg printers could <a id="page_13">have used moveable musical type for these small-scale publications, but such type was expensive and demanded particular typesetting skills. Furthermore, if music was included, the staves would take up significant space on the pages and reduce the number of folk songs that could be printed on eight or sixteen octavo pages. Economics clearly militated against the use of the newer technology. But there was a more fundamental reason why these imprints did not include musical notation—namely, to a large degree it was an impossibility. This is because the transmission was predominantly oral rather than literate, the melodies were committed to memory rather than to paper. Only much later did some of the melodies come to be written down and ultimately to be printed, but they represent only a small fraction of the large number of melodies that were widely sung in the early sixteenth century.</a></p><a id="page_13">
</a><p class="Body"><a id="page_13">Singing was by ear rather than by eye. Instead of appearing in musical notation, the individual melodies were known by a system of codes and identifiers that acted as a kind of aide-mémoire, enabling the singers to call to mind each specific tune. On the one hand, the first line of a prominent song became the name of its associated tune, so that the melody, orally transmitted, was identified by its original text, even though the text about to be sung might be totally different from the stanzas that originated with the melody in the first place. For example, one of the imprints in Herder’s collection has the following on the title page: “Ein ander lied von eim Jäger . . . Im thon als man singt des <i>Frawen lob</i>. <i>Der Waldt hat sich entlaubet</i>” (A second song of a hunter . . . to the tune of Frauenlob’s “The Trees Have Lost Their Leaves”).<sup></sup></a><a href="#/xhtml/#rfn30" id="fn30">30</a> So the song of the hunter is to be sung to the tune that was created for a poem of thwarted love and evil gossip. Similarly, the heading of no. 18 in first imprint bound in <i>Das Weimarer Liederbuch</i> reads: “Eyn newen Bergkreye . . . Im thon/ <i>Ach Got in deynem höchsten thron</i>” (A new miners’ song . . . to the tune “Ah God, in Thy Highest Throne”). But the narrative song begins:</p>
<table class="table1">
<tbody><tr>
<td>In Gottes namen fahren wir an</td>
<td>In God’s name we begin,</td>
</tr>
<tr>
<td>das best das wir gelernet han</td>
<td>the best that we have learned,</td>
</tr>
<tr>
<td>ein newes Lied zu singen</td>
<td>a new song to sing,</td>
</tr>
<tr>
<td>von Hertzog Friderich hochgeporn . . .<sup><a href="#/xhtml/#rfn31" id="fn31">31</a></sup></td>
<td>of Duke Frederic, high-born . . .</td>
</tr>
</tbody></table>
<p class="Body1"><a id="page_14">On the other hand, the melodies could be identified by names or other kinds of labels. Some were named after particular people, frequently those who first figured in an original folk song, such as <i>Hertzog Ernst thon</i> (tune),<sup></sup></a><a href="#/xhtml/#rfn32" id="fn32">32</a> or named after a general profession such as <i>Wächterleid</i> (Watchman’s song).<sup><a href="#/xhtml/#rfn33" id="fn33">33</a></sup> But often the melodies were named after famous Meistersinger poets, such as Barthel Regenbogen (d. soon after 1318), who had a whole family of tunes associated with his name;<sup><a href="#/xhtml/#rfn34" id="fn34">34</a></sup> Reinmar von Brennenberg (or Bremberger), thirteenth-century bishop of Regensburg;<sup><a href="#/xhtml/#rfn35" id="fn35">35</a></sup> and Jörg Schiller (fl. 1453–62),<sup><a href="#/xhtml/#rfn36" id="fn36">36</a></sup> among others.</p>
<p class="H1"><i>M<span class="small">EISTERGESANG</span></i></p>
<p class="Bodyflush1">The fact that tune names were specifically linked to notable past Meistersingers demonstrates the overlap between the relatively simple popular<sup><a href="#/xhtml/#rfn37" id="fn37">37</a></sup> folk songs and the more complex poetry and music of the monodic art-song tradition of the Meistergesang. Growing out of the earlier courtly poetry and music of the Minnesingers, Meistergesang—the art of composing and performing Meisterlieder by Meistersingers—was established in major German cities in the early fourteenth century, flourished in the sixteenth century, and continued well into the seventeenth century and even beyond.<sup><a href="#/xhtml/#rfn38" id="fn38">38</a></sup> Like the merchant and craft guilds, which set the standards for membership and in return preserved <a id="page_15">the rights and privileges of guild members, the Meistersingers also formed themselves into guild-like societies<sup></sup></a><a href="#/xhtml/#rfn39" id="fn39">39</a> that drew up specific poetic and musical rules for the creation of Meisterlieder, and similarly directed how such Meisterlieder were to be performed by the Meistersingers.</p>
<p class="Body">From the beginning there was a strong religious element in the poetry of the Meistersingers,<sup><a href="#/xhtml/#rfn40" id="fn40">40</a></sup> who thought of themselves as continuing the expertise demonstrated by King David in the biblical Psalms. But as the tradition expanded, so did the topics for poetic exploration. Nevertheless, religious themes continued to be a major focus of the Meistersingers.</p>
<p class="Body">The poetic and musical form employed by the Meistersingers was the stanzaic “bar” or “bar form,” a term that has an obscure background but that seems to have been derived from the art of fencing, where <i>barat</i> meant a well-aimed thrust,<sup><a href="#/xhtml/#rfn41" id="fn41">41</a></sup> and thus in the Meistersinger context the emphasis was upon skill, both poetic and musical. In later usage “bar form” designated the structural form of the stanza, whereas for the Meistersingers <i>Bar</i> was understood to encompass the complete Meisterlied stanzas (always three in number) with their music. Poetically, the bar-form stanza comprised two sections: the <i>Aufgesang</i> (before song) and the <i>Abgesang</i> (after song). The <i>Aufgesang</i> was itself divided into two equal sections (<i>Stollen</i>), with each of the <i>Stollen</i> comprising the same number of lines, metrical stress patterns, and syllables per line. The <i>Abgesang</i> had a different poetic structure from that of the <i>Stollen</i> of the preceding <i>Aufgesang,</i> with lines of different lengths and meters, and was frequently longer than the two <i>Stollen</i> combined.</p>
<p class="Body">Given here as an example of a typical poetic structure of a Meisterlied is the first stanza of <i>Gloria patry lob und er</i>, written by Hans Sachs in 1514 (<i>Aufgesang</i>, lines 1–12, with lines 1–6 for the first <i>Stollen</i> and lines 7–12 for the second; <i>Abgesang</i>, lines 13–27):</p>
<table class="table1 tablel">
<tbody><tr>
<td>1</td>
<td>Gloria patry lob und er</td>
</tr>
<tr>
<td></td>
<td>got vatter in dem dron</td>
</tr>
<tr>
<td></td>
<td>et fillio dem sün so her</td>
</tr>
<tr>
<td></td>
<td>et spiritüi sancto fron</td>
</tr>
<tr>
<td><a id="page_16">5</a></td>
<td>lab er sey dem heilligen geist</td>
</tr>
<tr>
<td></td>
<td>drey nommen und ein got genent</td>
</tr>
<tr>
<td></td>
<td>Sicüt erat und ymer mer</td>
</tr>
<tr>
<td></td>
<td>in principio schon</td>
</tr>
<tr>
<td></td>
<td>der ye was et nuncet semper</td>
</tr>
<tr>
<td>10</td>
<td>in secüla on abelon</td>
</tr>
<tr>
<td></td>
<td>secülor um aüch aller meist</td>
</tr>
<tr>
<td></td>
<td>von welt zw welt/ ewig un ent.</td>
</tr>
<tr>
<td></td>
<td class="tda">Got vatter seinen sün gepirt</td>
</tr>
<tr>
<td></td>
<td class="tda">hoc in der drinidat</td>
</tr>
<tr>
<td>15</td>
<td class="tda">Wie das geschicht kein lerer das beschriben hat</td>
</tr>
<tr>
<td></td>
<td class="tda">Auf disser erden kreis</td>
</tr>
<tr>
<td></td>
<td class="tda">Kein mensch aüch des nie wirdig wür</td>
</tr>
<tr>
<td></td>
<td class="tda">der disse ding gancz grüntlich weis</td>
</tr>
<tr>
<td></td>
<td class="tda">Disse gepürt verporgen ist</td>
</tr>
<tr>
<td>20</td>
<td class="tda">Den siben künsten frey</td>
</tr>
<tr>
<td></td>
<td class="tda">geamatrey</td>
</tr>
<tr>
<td></td>
<td class="tda">rhetorica philossoffy</td>
</tr>
<tr>
<td></td>
<td class="tda">loics und astronomey</td>
</tr>
<tr>
<td></td>
<td class="tda">gramatya mit müsica</td>
</tr>
<tr>
<td>25</td>
<td class="tda">mit irer süssen melodey</td>
</tr>
<tr>
<td></td>
<td class="tda">da mit al meister sind gespeist</td>
</tr>
<tr>
<td></td>
<td class="tda">Haben klerlich nie erkent.<sup><a href="#/xhtml/#rfn42" id="fn42">42</a></sup></td>
</tr>
</tbody></table>
<table class="table1 tablel">
<tbody><tr>
<td class="tdt">1</td>
<td class="tdt"><i>Gloria patri</i> praise and honor</td>
</tr>
<tr>
<td></td>
<td>[to] God the Father on the throne</td>
</tr>
<tr>
<td></td>
<td><i>et filio</i> the Son and Lord</td>
</tr>
<tr>
<td></td>
<td><i>et spiritui sancto</i> glorious</td>
</tr>
<tr>
<td>5</td>
<td>praise be the Holy Spirit</td>
</tr>
<tr>
<td></td>
<td>called three names but one God.</td>
</tr>
<tr>
<td></td>
<td><i>Sicut erat</i> and ever more</td>
</tr>
<tr>
<td></td>
<td><i>in principio</i> already</td>
</tr>
<tr>
<td></td>
<td>who ever was <i>et nunc et semper</i></td>
</tr>
<tr>
<td>10</td>
<td><i>in secula</i> without ceasing</td>
</tr>
<tr>
<td></td>
<td><i>seculorum</i> and most of all in everything</td>
</tr>
<tr>
<td></td>
<td>from age to age without end.</td>
</tr>
<tr>
<td></td>
<td class="tda">God the Father begot his Son</td>
</tr>
<tr>
<td></td>
<td class="tda">high in the Trinity</td>
</tr>
<tr>
<td><a id="page_17">15</a></td>
<td class="tda">how this happened no doctor in history has described</td>
</tr>
<tr>
<td></td>
<td class="tda">in the circle of this earth.</td>
</tr>
<tr>
<td></td>
<td class="tda">Nor would any man ever be worthy</td>
</tr>
<tr>
<td></td>
<td class="tda">to know this thing in a completely thorough way,</td>
</tr>
<tr>
<td></td>
<td class="tda">this birth is concealed.</td>
</tr>
<tr>
<td>20</td>
<td class="tda">The seven liberal arts</td>
</tr>
<tr>
<td></td>
<td class="tda">geometry,</td>
</tr>
<tr>
<td></td>
<td class="tda">rhetoric, philosophy,</td>
</tr>
<tr>
<td></td>
<td class="tda">logic and astronomy,</td>
</tr>
<tr>
<td></td>
<td class="tda">grammar, along with music</td>
</tr>
<tr>
<td>25</td>
<td class="tda">with its sweet melody—</td>
</tr>
<tr>
<td></td>
<td class="tda">by which [arts] all teachers are fed</td>
</tr>
<tr>
<td></td>
<td class="tda">never to understand this [the incarnation] clearly.</td>
</tr>
</tbody></table>
<p class="Bodyflush1">In carefully constructed lines Sachs moves from praise to the Godhead, in which his vernacular words are intertwined with the Latin text of the <i>Gloria Patri</i>, to the theology of the incarnation. Although this was written years before Luther’s protest about indulgences, Sachs nevertheless expressed a somewhat critical stance with regard to the theological scholasticism of the time.</p>
<p class="Body">Melodies of the Meisterlieder were, like folk-song melodies, almost exclusively orally transmitted and therefore are found in only relatively few sources.<sup><a href="#/xhtml/#rfn43" id="fn43">43</a></sup> But from the melodies that are known, they were, like the poetry they accompanied, similarly carefully well-crafted, being composed in an AAB form, following the double <i>Stollen</i> poetic structure of the <i>Aufgesang</i> in Meisterlieder. <a href="#/xhtml/#exp2.1">Example 2.1</a> is Hans Sachs’s <i>Silberweise</i> melody, composed in 1513,<sup><a href="#/xhtml/#rfn44" id="fn44">44</a></sup> given with the text of his <i>Salve ich grüss dich schone</i>,<sup><a href="#/xhtml/#rfn45" id="fn45">45</a></sup> written in 1515, another macaronic text that incorporates the complete Latin hymn <i>Salve Regina</i> within its three stanzas.<sup><a href="#/xhtml/#rfn46" id="fn46">46</a></sup> The <i>Silberweise</i> comprises 14 melodic phrases in the Ionian mode (akin to the modern major key) that are mostly within the span of an octave. A rhythmic flourish begins phrases 1, 4, and 14, which is echoed in phrase 12. Phrases 13 and 14 (the end of the <i>Abgesang</i>) repeat phrases 3 and 4 (the end of the <i>Stollen</i>). Phrases 7 and 8 repeat phrases 5 and 6, and phrase 12 repeats phrase 2, but with a rhythmic variation. The end of phrase 11 is the same as the end of phrase 2 but is transposed up a fifth. Note also that phrases <a id="page_18">5 and 7 are derived from phrase 3. All these details give the melody as a whole an organic unity that is achieved with an economy of means, all these elements contributing toward the memorability of the melody, an essential quality in this predominantly oral culture.</a></p><a id="page_18">
<div class="page">
<p class="image" id="exp2.1"><img alt="image" src="images/p018.jpg"/></p>
<p class="caption">Example 2.1. Hans Sachs. Melody: <i>Silberweise</i> (1513). Text: <i>Salve ich grüss dich schone</i> (1515).</p>
</div>
<p class="H1"><i>B<span class="small">ALLADEN</span></i></p>
</a><p class="Bodyflush1"><a id="page_18">Ballads were written on all kinds of subjects but tended to have a notable “news” element, though from the perspective of the twenty-first century, a good many of the ballads appear to be recording history rather than current events. The art song of the Meistersinger tradition was the model for the popular ballads that used bar form, significantly simplified: the <i>Stollen</i> were rarely longer than two lines, and the <i>Abgesang</i> was considerably shorter than was usually the case with Meisterlieder. From the middle of the fifteenth century, news ballads were generally written to a simple formula: first the hearers’ attention was aroused by the announcement of the song that was beginning; </a><a id="page_19">then background information was introduced, such as the location of the event and/or the personalities that were the subject of the song; after which the stanzas, which could be quite numerous, presented the details and import of the particular narrative. Although the substance of the sung narratives differed from each other, they nevertheless began in much the same way, often using a similar vocabulary, as the following representative fifteenth-century examples demonstrate:</a></p><a id="page_19">
<table class="table1">
<tbody><tr>
<td>(1457)</td>
<td></td>
</tr>
<tr>
<td>Nun wil ich aber heben an</td>
<td>Now will I begin</td>
</tr>
<tr>
<td>das allerbest und das ich kan</td>
<td>in the best way that I can;</td>
</tr>
<tr>
<td>ich wills gar frölich singen . . .<sup><a href="#/xhtml/#rfn47" id="fn47">47</a></sup></td>
<td>I will right joyfully sing . . .</td>
</tr>
<tr>
<td class="tdt">(1468)</td>
<td></td>
</tr>
<tr>
<td>Ein liedli wil ich heben an,</td>
<td>A song will I raise,</td>
</tr>
<tr>
<td>wilde mär han ich vernan . . .<sup><a href="#/xhtml/#rfn48" id="fn48">48</a></sup></td>
<td>unruly news that I have learned . . .</td>
</tr>
<tr>
<td class="tdt">(1491)</td>
<td></td>
</tr>
<tr>
<td>Nun wöll wir aber singen</td>
<td>Now well shall we sing</td>
</tr>
<tr>
<td>und wöllends heben an</td>
<td>and here will begin</td>
</tr>
<tr>
<td>von dem keiser Maximilian . . .<sup><a href="#/xhtml/#rfn49" id="fn49">49</a></sup></td>
<td>about Emperor Maximilian . . .</td>
</tr>
</tbody></table>
</a><p class="Body1"><a id="page_19">Ballads continued to be produced in every generation, and their formulaic nature meant that verbal correspondences were often not simply similar but verbatim, especially in the opening lines. For example, several texts<sup></sup></a><a href="#/xhtml/#rfn50" id="fn50">50</a> begin with the same two lines:</p>
<table class="table1">
<tbody><tr>
<td>Was wöll wir aber heben an</td>
<td>What shall we raise</td>
</tr>
<tr>
<td>ein neues lied zu singen.</td>
<td>in a new song to sing?</td>
</tr>
</tbody></table>
<p class="Bodyflush1">One will continue with:</p>
<table class="table1">
<tbody><tr>
<td><a id="page_20">Wol von dem könig aus Frankenreich,</a></td>
<td>of the king of Franconia</td>
</tr>
<tr>
<td>Mailand wolt er bezwingen . . .<sup><a href="#/xhtml/#rfn51" id="fn51">51</a></sup></td>
<td>who defeated Milan . . .</td>
</tr>
</tbody></table>
<p class="Bodyflush1">Another continues with:</p>
<table class="table1">
<tbody><tr>
<td>von einem frewlein hübsch und fein,</td>
<td>of a fraulein pretty and delicate,</td>
</tr>
<tr>
<td>ja wer ich bey jr dinnen . . .<sup><a href="#/xhtml/#rfn52" id="fn52">52</a></sup></td>
<td>yes, that I could dine with her . . .</td>
</tr>
</tbody></table>
<p class="Bodyflush1">Yet another has the following two lines:</p>
<table class="table1">
<tbody><tr>
<td>Wir singen von</td>
<td>We sing of a black monk</td>
</tr>
<tr>
<td class="tda">einen schwarzen mönch</td>
<td></td>
</tr>
<tr>
<td>und seiner naterinnen . . .<sup><a href="#/xhtml/#rfn53" id="fn53">53</a></sup></td>
<td>and his female viper . . .</td>
</tr>
</tbody></table>
<p class="Body1">After Luther’s protest in 1517 religious ballads, a notable part of the wider cultural tradition, became strongly polarized and polemical as the Reformation debate continued.<sup><a href="#/xhtml/#rfn54" id="fn54">54</a></sup></p>
<p class="H1"><i>L<span class="small">EISEN</span></i></p>
<p class="Bodyflush1">A distinctive religious folk song stemming from the early medieval period was the Leise, a term derived from <i>Kyrie eleison</i>, which concluded such songs, either abbreviated or contracted: Kyrieleison, Kyrio-leis, Kyrieleis, Kyriel.<sup><a href="#/xhtml/#rfn55" id="fn55">55</a></sup> These folk songs were mostly single-stanza, paraliturgical refrains sung in processions, pilgrimages, funerals, and liturgical dramas, closely associated with the major festivals of the church year and frequently being translations or paraphrases of Latin liturgical texts. Unlike many other religious vernacular <a id="page_21">songs that, because of oral transmission, either have relatively few written sources or have disappeared altogether, Leisen are found in quite a number of documentary sources.</a></p><a id="page_21">
<p class="Body">One of the earliest Leisen, dating from the eleventh century, is <i>Christ ist erstanden</i>, related both textually and musically to the Easter Sequence <i>Victimae paschali laudes</i>.</p>
<table class="table1">
<tbody><tr>
<td>Christ der ist erstanden</td>
<td>Christ is arisen</td>
</tr>
<tr>
<td>von der marter alle.</td>
<td>from all torments.</td>
</tr>
<tr>
<td>Des sull wir alle fro sein</td>
<td>Then shall we all be joyful,</td>
</tr>
<tr>
<td>christ sol unser trost dein.</td>
<td>Christ be our comfort.</td>
</tr>
<tr>
<td>Kyriel.<sup><a href="#/xhtml/#rfn56" id="fn56">56</a></sup></td>
<td>Kyrieleis.</td>
</tr>
</tbody></table>
</a><p class="Bodyflush1"><a id="page_21">This Leise was extremely popular: Walther Lipphardt records that it is found in 109 manuscripts dating between the twelfth and fifteenth century, with a further 51 manuscripts and printed sources dating from the first half of the sixteenth century.<sup></sup></a><a href="#/xhtml/#rfn57" id="fn57">57</a> The earliest source is a twelfth-century Salzburg manuscript of an Easter liturgical drama. Toward the end is the following: “Chorus. <i>Surrexit enim sicut</i>. Populus. <i>Christ is erstanden von der mar-</i>[<i>ter alle</i>]”;<sup><a href="#/xhtml/#rfn58" id="fn58">58</a></sup> that is, after the choir has sung the Easter Antiphon, the people sing the Leise <i>Christ ist erstanden</i>. The fact that the manuscript includes only an abbreviated first line underscores the oral nature of the singing. Similarly, the many variant forms of the Leise witness to its wide oral transmission,<sup><a href="#/xhtml/#rfn59" id="fn59">59</a></sup> to which the extant manuscripts can give only partial witness. The Leise continued in popularity, and Luther thought highly of this form, commending it in church from the pulpit and at home from the dining table. For example, in a sermon on the First Sunday after Easter 1523, he said:</p>
<p class="EX">This is what is being sung everywhere at this time in the hymn of the Lord’s resurrection. But not everyone understands it. He who wrote it has the right understanding, for he does not stop at the Lord’s resurrection when he says <a id="page_22">“Christ ist erstanden, etc.” as if that is enough, but brings it home to us by continuing “des sollen wir alle fro sein” [then shall we all be joyful].<sup></sup></a><a href="#/xhtml/#rfn60" id="fn60">60</a></p>
<p class="Bodyflush1">At his dining table sometime during the 1530s Luther remarked: “All the songs one sings over time become tiring, but <i>Christ ist erstanden</i> one can sing all the year long.”<sup><a href="#/xhtml/#rfn61" id="fn61">61</a></sup> Such references, especially those in his sermons, are a measure of the popularity of the hymn. Luther could refer to it because he knew that it was in the common memory of his congregation.</p>
<p class="Body">The Leise <i>In Gottes Namen fahren wir</i> has its origins in the twelfth century. It was a pilgrim hymn sung by devout Germans on their way to Jerusalem. Later it was also associated with funeral processions, often in connection with <i>Gott der Vater wohn uns bei</i> (see below), and circulated in variant forms:<sup><a href="#/xhtml/#rfn62" id="fn62">62</a></sup></p>
<table class="table1">
<tbody><tr>
<td>In gotes namen fara wir,</td>
<td>In God’s name we journey,</td>
</tr>
<tr>
<td>seyner genaden gara wir,</td>
<td>and for his grace we implore,</td>
</tr>
<tr>
<td>Nu helffe uns die gotes krafft</td>
<td>now our help be God’s power</td>
</tr>
<tr>
<td>und das heylig grab,</td>
<td>and the holy grave</td>
</tr>
<tr>
<td>da got selber ynne lag.</td>
<td>where God himself had lain.</td>
</tr>
<tr>
<td>Kyrieleys.<sup><a href="#/xhtml/#rfn63" id="fn63">63</a></sup></td>
<td>Kyrieleis.</td>
</tr>
</tbody></table>
<p class="Body1">The Pentecost Leise <i>Nun bitten wir den Heiligen Geist</i> appeared in the thirteenth century, the earliest source being a sermon by Berthold von Regensburg, who called it a “kyrleise” and quoted it as a known song:</p>
<table class="table1">
<tbody><tr>
<td>Nû biten wir den heiligen geist</td>
<td>Now pray we the Holy Ghost</td>
</tr>
<tr>
<td>umb den rechten glouben aller meist,</td>
<td>for true faith most of all,</td>
</tr>
<tr>
<td>daz er uns behüete an unserm ende,</td>
<td>that he preserve us at our end,</td>
</tr>
<tr>
<td>sô wir heim suln varn ûz disem ellende.</td>
<td>when we depart home from this misery.</td>
</tr>
<tr>
<td>kyrieleis.<sup><a href="#/xhtml/#rfn64" id="fn64">64</a></sup></td>
<td>Kyrieleis.</td>
</tr>
</tbody></table>
<p class="Bodyflush1"><a id="page_23">This Leise is closely related to the Pentecost Sequence <i>Veni sancte spiritus</i> and may well have been sung either in alternation with or following the Pentecost Sequence at least in some churches in the later decades of the fifteenth century, as indeed <i>Christ ist erstanden</i> may have been sung in alternation with the Easter Sequence <i>Victimae paschali laudes</i>. <i>Nun bitten wir den Heiligen Geist</i> was a Leise that Luther quoted and expounded at various times from the pulpit, again drawing on the common memory of his people.<sup></sup></a><a href="#/xhtml/#rfn65" id="fn65">65</a></p>
<p class="Body">Another vernacular song, not strictly a Leise but rather a translation of the Latin Christmas hymn <i>Dies et laetitiae in ortu regali</i>,<sup><a href="#/xhtml/#rfn66" id="fn66">66</a></sup> dates from the early thirteenth century. The German translation—<i>Der Tag, der ist so freudenreich</i><sup><a href="#/xhtml/#rfn67" id="fn67">67</a></sup>—appeared around the same time. Though nominally in four stanzas, it was expanded by further stanzas, which were not always given in the same order in the various sources. But one of the stanzas—the second of the original four—was frequently sung on its own and effectively became a Leise:</p>
<table class="table1">
<tbody><tr>
<td>Ein Kindelein so löbigkleich</td>
<td>A little child so laudable</td>
</tr>
<tr>
<td>ist uns geboren heute</td>
<td>is for us born today</td>
</tr>
<tr>
<td>Von ainer jungfraw seuberleich,</td>
<td>from a pure virgin,</td>
</tr>
<tr>
<td>zu trost uns armen leuten:</td>
<td>to comfort us poor people:</td>
</tr>
<tr>
<td>Wer uns das kindlein nit geporn,</td>
<td>Was not the child for us born,</td>
</tr>
<tr>
<td>so wer wir all zu mal verlorn,</td>
<td>we who were all together lost</td>
</tr>
<tr>
<td>das hail ist unser aller.</td>
<td>the salvation of us all.</td>
</tr>
<tr>
<td>Eya du süesser Jhesu Christ,</td>
<td>Ei, thou sweet Jesus Christ,</td>
</tr>
<tr>
<td>seit du mensch geboren bist,</td>
<td>since thou art born as man,</td>
</tr>
<tr>
<td>behüet uns vor der helle!<sup><a href="#/xhtml/#rfn68" id="fn68">68</a></sup></td>
<td>protect us from hell.</td>
</tr>
</tbody></table>
<p class="Bodyflush1">Luther frequently referred to <i>Ein Kindelein so löbelich</i> and cited it in detail in his Christmas sermons throughout his ministry in Wittenberg, drawing again and again on the content of the single stanza, which the people knew by heart and sang from memory.<sup><a href="#/xhtml/#rfn69" id="fn69">69</a></sup></p>
<p class="Body"><a id="page_24">The Pentecost Antiphon <i>Veni sancte spiritus</i> was translated into German in the fourteenth century as follows:</a></p><a id="page_24">
<table class="table1">
<tbody><tr>
<td>Kum heiliger geyst, herre gott,</td>
<td>Come, Holy Spirit, Lord God,</td>
</tr>
<tr>
<td>erfüll unß mit deinen gnaden gut,</td>
<td>fulfill us well with your grace,</td>
</tr>
<tr>
<td>deiner glaubigen hertz, mut und synn,</td>
<td>thy faithful heart, fortitude and mind,</td>
</tr>
<tr>
<td>inbrünstige lieb entzund in inn,</td>
<td>fervent love on fire in us,</td>
</tr>
<tr>
<td>der du durch deines liechtes glanst</td>
<td>from thee shines thy light;</td>
</tr>
<tr>
<td>in einem glauben gesamlet hast,</td>
<td>in one faith thou hast assembled</td>
</tr>
<tr>
<td>das volck auß aller welt und zungen,</td>
<td>the people from all the world and tongues,</td>
</tr>
<tr>
<td>das sey dir lieber herr zu lob und </td>
<td>to sing to thee, dear Lord,</td>
</tr>
<tr>
<td class="tda">ehr gesungen.</td>
<td class="tda">praise and glory.</td>
</tr>
<tr>
<td>Alleluia, alleluia.<sup><a href="#/xhtml/#rfn70" id="fn70">70</a></sup></td>
<td>Alleluia, alleluia.</td>
</tr>
</tbody></table>
</a><p class="Bodyflush1"><a id="page_24">This Leise made a particular impression on Luther, leading him to speak in somewhat extravagant terms: “The hymn <i>Komm, heiliger Geist</i> was written by the Holy Spirit himself, both words and melody.”<sup></sup></a><a href="#/xhtml/#rfn71" id="fn71">71</a></p>
<p class="Body">The Christmas Sequence <i>Grates nunc omnes</i> gave rise to the vernacular Leise <i>Gelobet seist du, Jesu Christ</i> in the late fourteenth century. The earliest form is in Plattdeutsch (Low German):</p>
<p class="verse-line01">Lovet sistu Ihesu Crist</p>
<p class="verse-line01a">dat du hude ghebaren bist,</p>
<p class="verse-line01a">van eyner maghet. Dat is war.</p>
<p class="verse-line01a">Des frow sik alde hemmelische schar.</p>
<p class="verse-line01a">Kyrieleis.<sup><a href="#/xhtml/#rfn72" id="fn72">72</a></sup></p>
<p class="Bodyflush1">In Hochdeutsch (High German) this verse became:</p>
<table class="table1">
<tbody><tr>
<td>Gelobet seistu, Jesu Christ,</td>
<td>Praise to thee, Jesus Christ,</td>
</tr>
<tr>
<td>das du mensch geboren bist,</td>
<td>that thou wast born a man</td>
</tr>
<tr>
<td>von einer jungfroun, daz ist war,</td>
<td>from a virgin, this is true;</td>
</tr>
<tr>
<td><a id="page_25">des freuet sich aller engel schar.</a></td>
<td>the angel host rejoices.</td>
</tr>
<tr>
<td>Kyrieleis.<sup><a href="#/xhtml/#rfn73" id="fn73">73</a></sup></td>
<td>Kyrieleis.</td>
</tr>
</tbody></table>
<p class="Body1">Around the same time, in the late fourteenth century, another Leise appeared, a vernacular version of the Corpus Christi Sequence <i>Lauda Sion</i>. It was commonly sung in funeral processions:<sup><a href="#/xhtml/#rfn74" id="fn74">74</a></sup></p>
<table class="table1">
<tbody><tr>
<td>God sij gelobbet und gebenedyet</td>
<td>God be praised and blessed</td>
</tr>
<tr>
<td>der onß alle hayt gespijßit</td>
<td>that he has fed everyone</td>
</tr>
<tr>
<td>mydt synem fleysch, mydt synem blude,</td>
<td>with his flesh, and with his blood,</td>
</tr>
<tr>
<td>das gibbe unß lieber herre got zu gude</td>
<td>so give our dear Lord all good;</td>
</tr>
<tr>
<td>das heylge sacramente</td>
<td>the holy Sacrament</td>
</tr>
<tr>
<td>an unßerm lesten ende</td>
<td>at our last end [to receive]</td>
</tr>
<tr>
<td>uß des gewijten pristers hende.</td>
<td>from the consecrated priest’s hands.</td>
</tr>
<tr>
<td>Kyrieleyson.<sup><a href="#/xhtml/#rfn75" id="fn75">75</a></sup></td>
<td>Kyrieleyson.</td>
</tr>
</tbody></table>
<p class="Body1">The creedal Leise (even though it has none of the usual refrains) <i>Wir glauben all an einen Gott</i>, dating from around the beginning of the fifteenth century, was based on a Latin partial paraphrase of the Apostolic and Nicene Creeds:</p>
<table class="table1">
<tbody><tr>
<td>Credo in deum</td>
<td>I believe in God, Father Almighty.</td>
</tr>
<tr>
<td class="tda">patrem omnipotentem.</td>
<td></td>
</tr>
<tr>
<td>Credo et in filium</td>
<td>I believe also in the Son,</td>
</tr>
<tr>
<td>sanctum dominum</td>
<td>the holy Lord,</td>
</tr>
<tr>
<td>patri natura uniformen.</td>
<td>of the same nature as the Father.</td>
</tr>
<tr>
<td>Credo et in spiritum</td>
<td>I believe also in the Spirit,</td>
</tr>
<tr>
<td>peccatorumque paraclitum</td>
<td>the Comforter of sinners,</td>
</tr>
<tr>
<td>utrique consubstancialem,</td>
<td>the same substance of both</td>
</tr>
<tr>
<td></td>
<td class="tda">[Father and Son],</td>
</tr>
<tr>
<td>trinitatem individuam</td>
<td>undivided Trinity</td>
</tr>
<tr>
<td>ab utroque fluentem</td>
<td>proceeding from both</td>
</tr>
<tr>
<td>et in essentia unum.<sup><a href="#/xhtml/#rfn76" id="fn76">76</a></sup></td>
<td>and in essence one.</td>
</tr>
</tbody></table>
<p class="Bodyflush1">At some later date a German text was created:</p>
<table class="table1">
<tbody><tr>
<td><a id="page_26">Wyr gelauben all in eynen got,</a></td>
<td>We all believe in one God,</td>
</tr>
<tr>
<td>schoffer himels und der erden</td>
<td>Creator of heaven and earth,</td>
</tr>
<tr>
<td>uns zcu trost gegeben,</td>
<td>who has given us consolation;</td>
</tr>
<tr>
<td>alle ding, di sten seym gepott;</td>
<td>all things stand by his command.</td>
</tr>
<tr>
<td>von der keusch war er geporen,</td>
<td>Of a virgin was he born,</td>
</tr>
<tr>
<td>maria, der zcarten auserkoren</td>
<td>Mary the tender was chosen,</td>
</tr>
<tr>
<td>unß zcu trost und aller kristenheyt,</td>
<td>to comfort us and all Christendom;</td>
</tr>
<tr>
<td>fur uns er wolde leyden</td>
<td>for us he willed to suffer</td>
</tr>
<tr>
<td>das wir mochten vormeyden</td>
<td>that we should escape</td>
</tr>
<tr>
<td>swere bein des tods der ewickeicht.<sup><a href="#/xhtml/#rfn77" id="fn77">77</a></sup></td>
<td>oppressive pain of eternal death.</td>
</tr>
</tbody></table>
<p class="Bodyflush1">The German version is not a strict translation of the Latin text but rather a newly written stanza that paraphrases the first two paragraphs of the two primary creeds. What is notable is the change from first person singular (<i>Credo</i>, “I believe”) to first person plural (<i>Wir gelauben</i>, “we believe”), implying corporate use.<sup><a href="#/xhtml/#rfn78" id="fn78">78</a></sup></p>
<p class="Body">Another vernacular song dating from the mid-fifteenth century is the version of the Antiphon <i>Media vita</i> that became known as <i>Mitten wir im leben sind</i>. Although it has the usual Leise refrain, it has several stanzas, rather than the usual one (though there is much repetition in each of the stanzas). And it is structured more as a kind of litany rather than a direct translation of the Latin Antiphon:<sup><a href="#/xhtml/#rfn79" id="fn79">79</a></sup></p>
<table class="table1">
<tbody><tr>
<td>In mittel unsers lebens zeyt</td>
<td>In the midst of our lifetime</td>
</tr>
<tr>
<td>im tod seind wir umbfangen:</td>
<td>in death are we surrounded:</td>
</tr>
<tr>
<td>Wen suchen wir der unß hilffe geyt,</td>
<td>for whom do we search for our help,</td>
</tr>
<tr>
<td>von dem wir huld erlangen,</td>
<td>from whom do we long for mercy</td>
</tr>
<tr>
<td>Dann dich, herr, alleine?</td>
<td>but from thee, Lord, alone?</td>
</tr>
<tr>
<td>der du umb unser missetat</td>
<td>Our misdeeds against thee</td>
</tr>
<tr>
<td>rechtlichen zürnen thust.</td>
<td>have justly angered thee.</td>
</tr>
<tr>
<td class="tda">Heiliger herre gott,</td>
<td class="tda">Holy Lord God,</td>
</tr>
<tr>
<td class="tda">Heiliger starcker gott,</td>
<td class="tda">Holy strong God,</td>
</tr>
<tr>
<td class="tda">Heiliger und barmhertziger heiler,</td>
<td class="tda">Holy and merciful Savior,</td>
</tr>
<tr>
<td class="tda">ewiger got,</td>
<td class="tda">Eternal God,</td>
</tr>
<tr>
<td><a id="page_27">laß unß nit gewalt thun</a></td>
<td>Let us not be overcome</td>
</tr>
<tr>
<td>des bitteren tods not.</td>
<td>in the depths of bitter death.</td>
</tr>
<tr>
<td class="tda">[Kyrieleison.]<sup><a href="#/xhtml/#rfn80" id="fn80">80</a></sup></td>
<td class="tda">Kyrieleison.</td>
</tr>
</tbody></table>
<p class="Body1">During the fifteenth century a votive Leise, first addressed to St. Peter (1422), then to the Virgin Mary (1480), was in circulation, which was eventually transformed into the Trinitarian hymn <i>Gott der Vater wohn uns bei</i>. The following is the version found in a Halberstadt manuscript dating from around 1500:</p>
<table class="table1">
<tbody><tr>
<td>Sancta maria won unß bey</td>
<td>Saint Maria, live with us</td>
</tr>
<tr>
<td>und laß unß nicht verterben,</td>
<td>and let us not perish;</td>
</tr>
<tr>
<td>mach unß von allen synden frey</td>
<td>make us free from all sins,</td>
</tr>
<tr>
<td>und wenn wyr sollen sterben,</td>
<td>and when we should die,</td>
</tr>
<tr>
<td>vom dem teufel unß bewar;</td>
<td>preserve us from the devil;</td>
</tr>
<tr>
<td>hilft, rayne mayt maria.</td>
<td>help, pure virgin Mary.</td>
</tr>
<tr>
<td>Hilf uns an der lieben engel schar,</td>
<td>Help us to the dear angel host,</td>
</tr>
<tr>
<td>Szo syngen wir alleluia.</td>
<td>for us to sing Alleluia.</td>
</tr>
<tr>
<td>Alleluia singen wir </td>
<td>Alleluia we sing</td>
</tr>
<tr>
<td>dem almechtigen got zu lobe</td>
<td>the Almighty God to praise;</td>
</tr>
<tr>
<td>Unß gyb, Herr, unß zu lone</td>
<td>to us give, Lord, to us present</td>
</tr>
<tr>
<td>dy hymmelische krone.</td>
<td>the heavenly crown.</td>
</tr>
<tr>
<td class="tda">Kyrieleyson, Christeeleyson;</td>
<td class="tda">Kyrieleison, Christe eleison;</td>
</tr>
<tr>
<td class="tda">gelobet seystu, maria.<sup><a href="#/xhtml/#rfn81" id="fn81">81</a></sup></td>
<td class="tda">Praise to thee, Mary.</td>
</tr>
</tbody></table>
<p class="Body1">The popularity of the vernacular song encouraged Luther later on to change the Marian theology into Trinitarian theology, and to use the strophic structure and vocabulary, together with its melody, in order to create a new hymn.</p>
<p class="Body">Many other vernacular songs were circulating at the beginning of the sixteenth century,<sup><a href="#/xhtml/#rfn82" id="fn82">82</a></sup> but the ones referred to here are those that had a continuing significance in the Reformation movement that was about to begin.</p>
<p class="Body"><a id="page_28">All these forms of vernacular singing—folk song, Meistergesang, ballads, and Leisen—were known to Luther. He had a lifelong love of and involvement with music.<sup></sup></a><a href="#/xhtml/#rfn83" id="fn83">83</a> He was knowledgeable about it, was able to compose, and was experienced in singing with others.<sup><a href="#/xhtml/#rfn84" id="fn84">84</a></sup> His knowledge and experience of Latin chant were extensive, having sung it as a schoolboy in Mansfeld, Magdeburg, and Eisenach, and as a monk and priest in Erfurt and Wittenberg. Luther was well aware that some of these Latin chants were the basis for vernacular religious songs, with the music often simplified and the Latin texts paraphrased into German verse.</p>
<p class="Body">The earliest folk songs Luther heard must have been those sung by his parents. A number of times he refers to his mother singing “For me and thee there is no favor, that is each our own fault.”<sup><a href="#/xhtml/#rfn85" id="fn85">85</a></sup> He also reports that, during his early years, his father would relax with a beer and would break out into song.<sup><a href="#/xhtml/#rfn86" id="fn86">86</a></sup> One of the songs he must have heard his parents sing, since it was intended to be sung on his name day, 11 November, was the song celebrating St. Martin of Tours, a song that was apparently popular at the end of the fifteenth century; indeed, it is most likely that it was one of the earliest vernacular songs that the young Martin learned to sing:</p>
<table class="table1">
<tbody><tr>
<td>Martin, lieber Herre,</td>
<td>Martin, dear Lord,</td>
</tr>
<tr>
<td>nun laß uns fröhlich sein.</td>
<td>now let us be joyful.</td>
</tr>
<tr>
<td>Heut zu deinen Ehren,</td>
<td>Today in thine honor,</td>
</tr>
<tr>
<td>und durch den Willen dein.</td>
<td>and by thy will,</td>
</tr>
<tr>
<td>Die Gans sollst du uns mehren</td>
<td>the goose thou shalt for us multiply</td>
</tr>
<tr>
<td>und den kühlen Wein:</td>
<td>and the cool wine:</td>
</tr>
<tr>
<td>gesotten und gebraten,</td>
<td>boiled and roasted,</td>
</tr>
<tr>
<td>sie müssen all herein.<sup><a href="#/xhtml/#rfn87" id="fn87">87</a></sup></td>
<td>they all must be here.</td>
</tr>
</tbody></table>
<p class="Body1"><a id="page_29">Hans Luther (or Luder) was a copper miner. The miners of Thuringia and Saxony had created, fostered, and promoted the so-called Bergreihen (mountain dances), also known as Bergmannslieder (miners’ songs). It seems likely that Luther heard his father sing some of the early Bergreihen and possibly even joined in singing some of these songs, not a few of which were religious in content.<sup></sup></a><a href="#/xhtml/#rfn88" id="fn88">88</a></p>
<p class="Body">While he was at the cathedral school in Magdeburg in 1497, it appears that Luther lived with the local community of the Brethren of the Common Life,<sup><a href="#/xhtml/#rfn89" id="fn89">89</a></sup> a community of the Devotio Moderna, the movement for simplicity in faith and life that was fostered among the Brethren, who were also strongly committed to education. Ulrike Hascher-Burger’s research has revealed that singing was a primary means for spiritual formation among the Brethren of the Common Life, and especially for the pupils in their schools.<sup><a href="#/xhtml/#rfn90" id="fn90">90</a></sup> Although much of this singing was with Latin texts, vernacular singing was also encouraged. The goal was “singing for blessedness,” the intensification of personal spirituality, a concern that was clearly evident in the young Luther.<sup><a href="#/xhtml/#rfn91" id="fn91">91</a></sup> It would seem that if this was indeed Luther’s experience in Magdeburg, it would have been a major influence on his thinking when he was later contemplating the extent and use of vernacular congregational song.</p>
<p class="Body">When Luther posted his 95 Theses on the eve of All Saints’ Day in 1517, European society in general, and German society in particular, was in a period <a id="page_30">of transition, changing from an oral to a literate culture. Within a matter of weeks the contents of his questions about indulgences were being discussed in universities across Europe. The transmission was not by word of mouth but by word in print, as printers scrambled to reprint these revolutionary propositions. What was happening in the academic and ecclesiastical worlds was also happening in popular culture. With the increasing availability of cheap, single-sheet imprints, it was now possible to read about all kinds of things that were formerly received only by ear. Printed matter, however, is useful only to those who have the ability to read, and since a significant proportion of the population was illiterate, both oral and print culture coexisted side by side in the first half of the sixteenth century and beyond. This was certainly the case with regard to vernacular song, both secular and religious. On the one hand, a significant number of German songs continued to circulate orally; on the other, an increasing number of such songs were also distributed individually in printed broadsheets or pamphlets. The surviving evidence for both means of transmission is in each case but the tip of an iceberg; what we see is but a small proportion of what once existed. There were many more orally transmitted songs than are preserved in the manuscript sources, songs that are now lost to us. Similarly, what we know about printed sources remains incomplete and fragmentary. All of the imprints preserved in Herder’s <i>Das Weimarer Liederbuch</i> apparently originated in Nuremberg, but Nuremberg was not the only place where there were printers. In addition to Wittenberg and Nuremberg there were at least twenty other cities with more than one printer in the first two decades of the sixteenth century,<sup></sup></a><a href="#/xhtml/#rfn92" id="fn92">92</a> and one or another of them could have produced such ephemeral pamphlets that have not survived. For example, the following printers produced part-books that include polyphonic settings of vernacular songs, indicating that they had access to both the texts and the melodies to have been able to have issued broadsides or pamphlets for more popular use: Arnt von Aich in Cologne (ca. 1512),<sup><a href="#/xhtml/#rfn93" id="fn93">93</a></sup> Erhart Oeglin in Augsburg <a id="page_31">(1512),<sup></sup></a><a href="#/xhtml/#rfn94" id="fn94">94</a> and Peter Schöffer in Mainz (1513).<sup><a href="#/xhtml/#rfn95" id="fn95">95</a></sup> In Strasbourg twelve imprints of vernacular songs issued between 1498 and 1524 have been recorded,<sup><a href="#/xhtml/#rfn96" id="fn96">96</a></sup> but given the later numerous popular song imprints,<sup><a href="#/xhtml/#rfn97" id="fn97">97</a></sup> the large number of printers active in the city,<sup><a href="#/xhtml/#rfn98" id="fn98">98</a></sup> and the hymnal production from 1524,<sup><a href="#/xhtml/#rfn99" id="fn99">99</a></sup> it would seem likely that more were printed in the early decades of the century than have survived. What was true for Strasbourg and Nuremberg is likely to have been repeated in other cities where there were printing presses.</p>
<p class="Body">In the early years of the sixteenth century many vernacular songs were in circulation, both orally and in print, both secular and religious, both old and new, a repertoire that soon began to include songs about Luther’s protest. One of the earliest was by Michael Stiefel (1486–1567),<sup><a href="#/xhtml/#rfn100" id="fn100">100</a></sup> like Luther, a former Augustinian monk and later the pastor in Esslingen.<sup><a href="#/xhtml/#rfn101" id="fn101">101</a></sup> His song <i>Johannes thut uns schrieben</i> had thirty-two stanzas and was published under the title <i>Von der Christförmigen, rechtgegründeten leer Doctoris Martini Luthers</i> (On the Christian and rightly grounded doctrine of Dr. Martin Luther; Augsburg: Oeglin, 1522, and reprints), to be sung “Jn brüder Veyten Thon” (in Brother Veit’s tune; see <a href="#/xhtml/#exp2.2">example 2.2</a>):<sup><a href="#/xhtml/#rfn102" id="fn102">102</a></sup></p>
<p class="image" id="exp2.2"><a id="page_32"><img alt="image" src="images/p032.jpg"/></a></p><a id="page_32">
<p class="caption">Example 2.2. Michael Stiefel. Text: <i>Joannes thut uns schrieben</i> (1522). Melody: <i>Brüder Veiten Ton</i>.</p>
<table class="table1">
<tbody><tr>
<td>1.</td>
<td></td>
</tr>
<tr>
<td>Joannes thut uns schrieben</td>
<td>John writes to us</td>
</tr>
<tr>
<td>von einem Engel klar,</td>
<td>clearly concerning an angel,</td>
</tr>
<tr>
<td>Der Gottes wort soll treiben</td>
<td>who is to convey God’s Word</td>
</tr>
<tr>
<td>ganz luter offenbar:</td>
<td>with utter plainness:<sup><a href="#/xhtml/#rfn103" id="fn103">103</a></sup></td>
</tr>
<tr>
<td>Zu uns thut sich auch scheiben,</td>
<td>it comes to us also,</td>
</tr>
<tr>
<td>es fält nit umb ein har,</td>
<td>it does not fail by a hair’s breadth,</td>
</tr>
<tr>
<td>daruff wil ich beleiben,</td>
<td>on this I will remain,</td>
</tr>
<tr>
<td>das sag ich euch furwahr. . . .</td>
<td>this I declare in truth. . . .</td>
</tr>
</tbody></table>
<table class="table1">
<tbody><tr>
<td>22.</td>
<td></td>
</tr>
<tr>
<td>On mich ir nichts vermegen,</td>
<td>Without me you can do nothing,</td>
</tr>
<tr>
<td>spricht Christus unser her:</td>
<td>says Christ our Lord;</td>
</tr>
<tr>
<td>Den willen müssz bewegen</td>
<td>the will must be moved</td>
</tr>
<tr>
<td>die gnad, nach Luthers ler.</td>
<td>by grace, according to Luther’s teaching.</td>
</tr>
<tr>
<td>Er mag sich selbs nit regen,</td>
<td>It cannot move itself,</td>
</tr>
<tr>
<td>doch ist die hilff nit fer,</td>
<td>yet help is not far;</td>
</tr>
<tr>
<td>sye kompt uns bald entgegen,</td>
<td>it will soon come to meet us,</td>
</tr>
<tr>
<td>als ich vom Luther her. . . .<sup><a href="#/xhtml/#rfn104" id="fn104">104</a></sup></td>
<td>as I hear from Luther. . . .</td>
</tr>
</tbody></table>
<p class="line"><img alt="line" src="images/line1.jpg"/></p>
</a><p class="foot" id="rfn1"><a id="page_32"></a><a href="#/xhtml/#fn1">1</a>. For example, the early-nineteenth-century American hymn tune M<span class="small">ORNING</span> S<span class="small">ONG</span> is a variant of both the early-eighteenth-century melody associated with the English nursery rhyme “Old King Cole” and the later “Bellman’s Song”; see Raymond F. Glover, ed., <i>The Hymnal 1982 Companion,</i> 3 vols. in 4 (New York: Church Hymnal Corporation, 1990–94), 3A:20–21.</p>
<p class="foot" id="rfn2"><a href="#/xhtml/#fn2">2</a>. An interesting example is the fourteenth-century “Five Joys of Mary,” which became the “Seven Joys of Mary” in the fifteenth century, then expanded further in the nineteenth century to become the “Nine Joys of Mary”; see Roy Palmer, ed., <i>Folk Songs Collected by Ralph Vaughan Williams</i> (London: Dent, 1983), 12 (no. 8).</p>
<p class="foot" id="rfn3"><a href="#/xhtml/#fn3">3</a>. See, for example, E. David Gregory, <i>Victorian Songhunters: The Recovery and Editing of English Vernacular Ballads and Folk Lyrics, 1820–1883</i> (Lanham: Scarecrow, 2006); E. David Gregory, <i>The Late Victorian Folk-Song Revival: The Persistence of English Melody, 1878–1903</i> (Lanham: Scarecrow, 2010). Perhaps the most significant anthology was the one originally published between 1882 and 1898 by Francis James Child, <i>The English and Scottish Popular Ballads</i> (New York: Dover, 1965); see also Bertrand Harris Bronson, <i>The Traditional Tunes of the Child Ballads with Their Texts, according to the Extant Records of Great Britain and America,</i> 4 vols. (Princeton: Prnceton University Press, 1959–72).</p>
<p class="foot" id="rfn4"><a href="#/xhtml/#fn4">4</a>. Depicted in the semi-autobiographical trilogy by Flora Thompson (1878–1947), especially her early years in the 1880s. Her comment, published in 1939, is telling: “Songs and singers all have gone, and in their places the wireless blares” (Flora Thompson, <i>Lark Rise to Candleford: A Trilogy</i> [1939–1943] [Boston: Godine, 2009], 64). The centrality of folk song was faithfully reflected in the BBC television adaptation of the books (2008–11).</p>
<p class="foot" id="rfn5"><a href="#/xhtml/#fn5">5</a>. See, for example, in America: Vance Randolph, ed., <i>Ozark Folk-Songs</i> (Columbia: State Historical Society of Missouri, 1946–50, revised reissue, Columbia: University of Missouri Press, 1980), and Alan Lomax, ed., <i>The Folk Songs of North America</i> (Garden City: Doubleday, 1960); in Denmark: Erik Dal, <i>Danske Visor: Gamle folkevisor. Skaemt. Efterklang</i> (Copenhagen: Rosenkilde &amp; Bagger, 1962), English edition, <i>Danish Ballads and Folk Songs</i>, trans. Henry Meyer (Copenhagen: Rosenkilde &amp; Bagger, 1967).</p>
<p class="foot" id="rfn6"><a href="#/xhtml/#fn6">6</a>. See, for example, Richard Leighton Greene, <i>The Early English Carols</i>, 2nd ed. (Oxford: Clarendon, 1977); Sarah Appleton Weber, <i>Theology and Poetry in the Middle English Lyric</i> (Columbus: Ohio State University Press, 1969); and especially Patrick S. Diehl, <i>The Medieval European Religious Lyric: An Ars Poetica</i> (Berkeley: University of California Press, 1985).</p>
<p class="foot" id="rfn7"><a href="#/xhtml/#fn7">7</a>. August Heinrich Hoffmann von Fallersleben, <i>Schlesische Volkslieder mit Melodien</i> (Leipzig: Breitkopf &amp; Härtel, 1842; repr., Hildesheim: Olms, 1973) and <i>Geschichte des deutschen Kirchenliedes bis auf Luthers Zeit</i>, 3rd ed. (Hannover: Rümpler, 1861; repr., Hildesheim: Olms, 1965); Rochus von Liliencron, <i>Die historischen Volkslieder der Deutschen vom 13. bis 16. Jahrhundert</i> (Leipzig: Vogel, 1865–69; repr., Hildesheim: Olms, 1966); Rochus von Liliencron, <i>Deutsches Leben im Volkslied um 1530</i> (Berlin: Spemann, 1884; repr., Hildesheim: Olms, 1966); Franz Magnus Böhme, <i>Altdeutsches Liederbuch: Volkslieder der Deutschen nach Wort und Weise aus dem 12. bis zum 17. Jahrhundert</i> [1887], 3rd ed. (Leipzig: Breitkopf &amp; Härtel, 1925; repr., Hildesheim: Olms, 1966). See also the more recent continuing series, sponsored by the Deutschen Volksliederarchiv, ed. John Meier et al., <i>Deutsche Volkslieder mit ihren Melodien</i> (Berlin: de Gruyter, 1935– ).</p>
<p class="foot" id="rfn8"><a href="#/xhtml/#fn8">8</a>. Johann Gottfried Herder, <i>Volkslieder</i> (Leipzig: Weigand, 1778–79; repr., Hildesheim: Olms, 1981).</p>
<p class="foot" id="rfn9"><a href="#/xhtml/#fn9">9</a>. Signatur 14, 6 : 60 e. A facsimile of the volume was published under the title <i>Das Weimarer Liederbuch: Schätzbare Sammlung alter Volkslieder</i>, ed. Konrad Kratzsch (Leipzig: Edition Leipzig, 1976; Zurich: Artemis, 1977), in which consecutive pagination is added.</p>
<p class="foot" id="rfn10"><a href="#/xhtml/#fn10">10</a>. A single folio sheet folded three times produces sixteen octavo pages; half a folio sheet folded twice produces eight pages.</p>
<p class="foot" id="rfn11"><a href="#/xhtml/#fn11">11</a>. See Albrecht Classen, ed., <i>Deutsche Liederbücher des 15. und 16. Jahrhunderts,</i> Volksliedstudien 1 (Münster: Waxmann, 2001); the content of <i>Das Weimarer Liederbuch</i> is recorded on pp. 282–302; an earlier inventory is found in K. Aue, “Verzeichnis zweier Sammlungen deutscher Volkslieder und Volksspiele auf fliegenden Blättern,” <i>Anzeiger für Kunde der deutschen Vorzeit</i> 8 (1839): 358–80.</p>
<p class="foot" id="rfn12"><a href="#/xhtml/#fn12">12</a>. One of the imprints is of Hans Sachs’s poem of praise for Nuremberg “Eyn Lobspruch der Stat Nürmberg,” printed by Kunegund Hergot, probably sometime around 1530; <i>Das Weimarer Liederbuch</i>, 113–27. On early Nuremberg printers, see Josef Benzing, <i>Die Buchdrucker des 16. und 17. Jahrhunderts im deutschen Sprachgebiet</i>, 2nd ed. (Wiesbaden: Harrassowitz, 1982), 350–60.</p>
<p class="foot" id="rfn13"><a href="#/xhtml/#fn13">13</a>. <i>Das Weimarer Liederbuch</i>, 1–78.</p>
<p class="foot" id="rfn14"><a href="#/xhtml/#fn14">14</a>. See WDKL 1:404 (no. 51). Georg Wachter married widow Kunegunde Hergot, who had inherited her husband’s Nuremberg printing house after Hans Hergot was executed in 1527 for publishing pro-Reformation literature.</p>
<p class="foot" id="rfn15"><a href="#/xhtml/#fn15">15</a>. <i>Das Weimarer Liederbuch</i>, 445–51. Although this imprint is undated, the same song was printed in Strasbourg by Matthias Schürer’s heirs, active between 1519 and 1528, and dated 1520; see Miriam Usher Chrisman, <i>Bibliography of Strasbourg Imprints, 1480–1599</i> (New Haven: Yale University Press, 1982), 191; and Benzing, <i>Die Buchdrucker des 16. und 17. Jahrhunderts</i>, 440.</p>
<p class="foot" id="rfn16"><a href="#/xhtml/#fn16">16</a>. <i>Das Weimarer Liederbuch</i>, 625–31.</p>
<p class="foot" id="rfn17"><a href="#/xhtml/#fn17">17</a>. <i>Das Weimarer Liederbuch</i>, 745–51.</p>
<p class="foot" id="rfn18"><a href="#/xhtml/#fn18">18</a>. See WDKL 3:86 (footnote to no. 114).</p>
<p class="foot" id="rfn19"><a href="#/xhtml/#fn19">19</a>. See Caroline Huey, <i>Hans Folz and Print Culture in Late Medieval Germany: The Creation of Popular Discourse</i> (Farnham: Ashgate, 2012); WDKL 2:830–38 (nos. 1048–50).</p>
<p class="foot" id="rfn20"><a href="#/xhtml/#fn20">20</a>. On Jörg Graff’s songs, see Daniel Trocmé-Latter, <i>The Singing of the Strasbourg Protestants, 1523–1531</i> (Farnham: Ashgate, 2015), 169–73; see also WDKL 3:371–77 (nos. 447–51).</p>
<p class="foot" id="rfn21"><a href="#/xhtml/#fn21">21</a>. See Eva Klesatschke, <i>Lienhard Nunnenbeck, die Meisterlieder und der Spruch: Edition und Untersuchungen</i> (Göppingen: Kümmerle, 1984); WDKL 2:1135 (no. 1402).</p>
<p class="foot" id="rfn22"><a href="#/xhtml/#fn22">22</a>. Frances H. Ellis, <i>The Early Meisterlieder of Hans Sachs</i> (Bloomington: Indiana University Press, 1974); WDKL 2:1139–43 (nos. 1403–10).</p>
<p class="foot" id="rfn23"><a href="#/xhtml/#fn23">23</a>. <i>Das Weimarer Liederbuch</i>, 713–19.</p>
<p class="foot" id="rfn24"><a href="#/xhtml/#fn24">24</a>. University Library, Munich: VD16 D 146.</p>
<p class="foot" id="rfn25"><a href="#/xhtml/#fn25">25</a>. <i>Das Weimarer Liederbuch</i>, 169–83.</p>
<p class="foot" id="rfn26"><a href="#/xhtml/#fn26">26</a>. <i>Das Weimarer Liederbuch</i>, 746–51. The typeset pages were presumably mispositioned when printed and are out of sequence. The correct order is 746, 749, 750, 747, 748, 751.</p>
<p class="foot" id="rfn27"><a href="#/xhtml/#fn27">27</a>. Luther published a small booklet to comfort the people in Bremen, where Heinrich von Zutphen had recently been preaching: “The Burning of Brother Henry” (1525); LW 32:265–86. It includes a narrative of Heinrich von Zutphen’s arrest and execution, together with an exposition of Psalm 9, which begins by using the metaphor of singing: “I certainly do not mind taking the time to explain this psalm briefly to you and to sing it with you.”</p>
<p class="foot" id="rfn28"><a href="#/xhtml/#fn28">28</a>. See the facsimile, <i>Martin Luther Deutsche Messe, 1526,</i> ed. Johannes Wolf (Kassel: Bärenreiter, 1934).</p>
<p class="foot" id="rfn29"><a href="#/xhtml/#fn29">29</a>. Donald W. Krummel and Stanley Sadie, eds., <i>Music Printing and Publishing</i> (New York: Norton, 1990), 18–24.</p>
<p class="foot" id="rfn30"><a href="#/xhtml/#fn30">30</a>. <i>Das Weimarer Liederbuch</i>, 585. See also Böhme, <i>Altdeutsches Liederbuch</i>, no. 259; the melody is also known as <i>Sehnsuch</i> (Longing) and is also found in the so-called <i>Lochheimer Liederbuch</i> (ca. 1450–60).</p>
<p class="foot" id="rfn31"><a href="#/xhtml/#fn31">31</a>. <i>Das Weimarer Liederbuch</i>, 26. The melody (also known as <i>Fränkischer Bauerton</i>), with an imprint dating from 1525 but clearly older, is given in Böhme, <i>Altdeutsches Liederbuch</i>, no. 390a.</p>
<p class="foot" id="rfn32"><a href="#/xhtml/#fn32">32</a>. Assigned to two songs in <i>Das Weimarer Liederbuch</i>, 153, 277; Böhme, <i>Altdeutsches Liederbuch</i>, no. 4. Later, Valentin Triller incorporated the melody as a hymn tune in <i>Ein Schlesich singbüchlein aus Göttlicher schrifft . . .</i> (Breslau: Scharffenberg, 1555); see Zahn 8804.</p>
<p class="foot" id="rfn33"><a href="#/xhtml/#fn33">33</a>. Alternative name for <i>Auß herttem we klagt ein held</i> (<i>Das Weimarer Liederbuch</i>, 397); see Böhme, <i>Altdeutsches Liederbuch</i>, no. 111.</p>
<p class="foot" id="rfn34"><a href="#/xhtml/#fn34">34</a>. <i>Langen Regenbogenthon</i> (<i>Das Weimarer Liederbuch</i>, 261, 499, 513); <i>Regenbogen breiff thon</i> (<i>Das Weimarer Liederbuch</i>, 713); <i>Regenbogen plaen thon</i> (<i>Das Weimarer Liederbuch</i>, 357); <i>Regenbogen Plaber thon</i> (<i>Das Weimarer Liederbuch</i>, 453); see WDKL 2:254–70 (nos. 418–29).</p>
<p class="foot" id="rfn35"><a href="#/xhtml/#fn35">35</a>. <i>Brembergers thon</i> (<i>Das Weimarer Liederbuch</i>, 303); Böhme, <i>Altdeutsches Liederbuch</i>, no. 23.</p>
<p class="foot" id="rfn36"><a href="#/xhtml/#fn36">36</a>. <i>Das Weimarer Liederbuch</i>, 445, 665; see WDKL 2:845 (nos. 1054–56).</p>
<p class="foot" id="rfn37"><a href="#/xhtml/#fn37">37</a>. The term “popular” is used here as elsewhere in its basic meaning “belonging to the people” (OED).</p>
<p class="foot" id="rfn38"><a href="#/xhtml/#fn38">38</a>. For the background, see Susan Gattuso, “Sixteenth-Century Nuremberg,” in <i>The Renaissance: From the 1470s to the End of the Sixteenth Century</i>, ed. Iain Fenlon <i></i>(London: Macmillan, 1989), 293–301, 303. Since the Meistergesang tradition has a long history that reaches into the nineteenth century, much of the literature embraces a wider chronology than is of interest here, that is, the late fifteenth century and the early decades of the sixteenth century.</p>
<p class="foot" id="rfn39"><a href="#/xhtml/#fn39">39</a>. Meistergesang members would also have belonged to many different craft guilds; see Table XI.4 in Gattuso, “Sixteenth-Century Nuremberg,” 300.</p>
<p class="foot" id="rfn40"><a href="#/xhtml/#fn40">40</a>. In a preface to his Meisterlieder, Hans Sachs states that Meistergesang was invented for the praise of God; <i>Hans Sachs Werke</i>, 26 vols., ed. Adelbert von Keller and Edmund Goetze (Stuttgart: Literarischen Verein, 1870–1908; repr., Hildesheim: Olms, 1964–82), 22:137.</p>
<p class="foot" id="rfn41"><a href="#/xhtml/#fn41">41</a>. See Richard Taruskin, <i>The Oxford History of Western Music,</i> 6 vols. (New York: Oxford University Press, 2005), 1:118.</p>
<p class="foot" id="rfn42"><a href="#/xhtml/#fn42">42</a>. Ellis, <i>The Early Meisterlieder of Hans Sachs</i>, 21–22.</p>
<p class="foot" id="rfn43"><a href="#/xhtml/#fn43">43</a>. See Horst Brunner, “Meistergesang,” in <i>New Grove Dictionary of Music and Musicians,</i> 2nd ed., ed. Stanley Sadie (New York: Grove, 2001), 16:294–300.</p>
<p class="foot" id="rfn44"><a href="#/xhtml/#fn44">44</a>. Ellis, <i>The Early Meisterlieder of Hans Sachs</i>, 314–15.</p>
<p class="foot" id="rfn45"><a href="#/xhtml/#fn45">45</a>. Ellis, <i>The Early Meisterlieder of Hans Sachs</i>, 71–72.</p>
<p class="foot" id="rfn46"><a href="#/xhtml/#fn46">46</a>. Sachs later reworked the text, changing the content from <i>Salve Regina</i> to <i>Salve Rex Christe</i>.</p>
<p class="foot" id="rfn47"><a href="#/xhtml/#fn47">47</a>. Liliencron, <i>Die historischen Volkslieder</i>, 1:501 (no. 108).</p>
<p class="foot" id="rfn48"><a href="#/xhtml/#fn48">48</a>. Liliencron, <i>Die historischen Volkslieder</i>, 1:553 (no. 121).</p>
<p class="foot" id="rfn49"><a href="#/xhtml/#fn49">49</a>. Liliencron, <i>Die historischen Volkslieder</i>, 2:300 (no. 300B). See also the example quoted on p. 13 above. For other examples, see Liliencron, <i>Die historischen Volkslieder</i> 1:555 (no. 122); 2:2, 10, 39, 128, 171, 271, 278, 355, 370, 378, 395, 402, 455 (nos. 125, 127, 133, 149, 161, 174, 176, 194, 197, 199, 205, 206B, 220).</p>
<p class="foot" id="rfn50"><a href="#/xhtml/#fn50">50</a>. Although the sources date from the second quarter of the sixteenth century, the content is often much older; see the next note.</p>
<p class="foot" id="rfn51"><a href="#/xhtml/#fn51">51</a>. Böhme, <i>Altdeutsches Liederbuch</i>, no. 389. Conrad, duke of Franconia was crowned king of Milan in 1128.</p>
<p class="foot" id="rfn52"><a href="#/xhtml/#fn52">52</a>. <i>Das Weimarer Liederbuch</i>, 289–90.</p>
<p class="foot" id="rfn53"><a href="#/xhtml/#fn53">53</a>. Böhme, <i>Altdeutsches Liederbuch</i>, no. 481.</p>
<p class="foot" id="rfn54"><a href="#/xhtml/#fn54">54</a>. See Rebecca Wagner Oettinger, <i>Music as Propaganda in the German Reformation</i> (Aldershot: Ashgate, 2001); Robert W. Scribner, <i>For the Sake of Simple Folk: Popular Propaganda for the German Reformation</i> (Oxford: Clarendon, 1994); Mark U. Edwards, <i>Printing, Propaganda, and Martin Luther</i> (Berkeley: University of California Press, 1994).</p>
<p class="foot" id="rfn55"><a href="#/xhtml/#fn55">55</a>. For the background, see the first part of the preface, Johannes Riedel, ed., <i>Leise Settings of the Renaissance and Reformation Era</i> (Madison: A-R Editions, 1980), vii–xii; for a long list of alternative endings to Leisen, see xx, n. 7.</p>
<p class="foot" id="rfn56"><a href="#/xhtml/#fn56">56</a>. Klostenneuburg Stiftsbibliothek ms. 1213, fol. 83<sup>v</sup>; photograph, Hansjakob Becker et al., eds., <i>Geistliches Wunderhorn: Große deutsche Kirchenlieder</i> (Munich: Becker, 2001), 29.</p>
<p class="foot" id="rfn57"><a href="#/xhtml/#fn57">57</a>. Walther Lipphardt, “<i>Christ ist erstanden</i>: Zur Geschichte des Liedes,” JbLH 5 (1960): 97–99.</p>
<p class="foot" id="rfn58"><a href="#/xhtml/#fn58">58</a>. Salzburg, Studienbibliothek ms. M II 6, fol. 67<sup>r</sup>; Lipphardt, “<i>Christ ist erstanden</i>,” photograph opposite p. 96.</p>
<p class="foot" id="rfn59"><a href="#/xhtml/#fn59">59</a>. WDKL 2:726–32 (nos. 936–40, 942–50)</p>
<p class="foot" id="rfn60"><a href="#/xhtml/#fn60">60</a>. WA 12:521; see also Martin Rößler, <i>Die Liedpredigt: Geschichte einer Predigtgattung</i> (Göttingen: Vandenhoeck &amp; Ruprecht, 1976), 21, for a comparison of the German version of the sermon with the Latin version in WA 11:96. For other examples of Luther citing <i>Christ ist erstanden</i> in sermons, see WA 20:335, 343; 37:29, 67; 46:315; 49:358–59.</p>
<p class="foot" id="rfn61"><a href="#/xhtml/#fn61">61</a>. WA TR 4, no. 4795; see also no. 4804; and also WA TR 5, no. 5415.</p>
<p class="foot" id="rfn62"><a href="#/xhtml/#fn62">62</a>. See Böhme, <i>Altdeutsches Liederbuch</i>, 677–80 (no. 568<sup>I–III</sup>); WDKL 2:515–17 (nos. 678–83).</p>
<p class="foot" id="rfn63"><a href="#/xhtml/#fn63">63</a>. WDKL 2:515 (no. 678).</p>
<p class="foot" id="rfn64"><a href="#/xhtml/#fn64">64</a>. Berthold von Regensburg, <i>Vollständige Ausgabe seiner Predigten</i>, 2 vols., ed. Franz Pfeiffer (Vienna: Braumüller, 1862–80; repr., Berlin: de Gruyter, 1965), 1:43.</p>
<p class="foot" id="rfn65"><a href="#/xhtml/#fn65">65</a>. See, for example, WA 4:602–3; 41:242; 47:739; see also WA TR 5, no. 5376.</p>
<p class="foot" id="rfn66"><a href="#/xhtml/#fn66">66</a>. WDKL 2:206–7 (no. 332).</p>
<p class="foot" id="rfn67"><a href="#/xhtml/#fn67">67</a>. WDKL 2:520–22 (nos. 573–74). Later Lutheran hymnals credit Luther as the author of the translation; for example, Gottfried Vopelius, <i>Neu Leipziger Gesangbuch</i> (Leipzig: Klinger, 1682), 34.</p>
<p class="foot" id="rfn68"><a href="#/xhtml/#fn68">68</a>. WDKL 2:520 (no. 690).</p>
<p class="foot" id="rfn69"><a href="#/xhtml/#fn69">69</a>. See, for example, WA 7:189–90; 9:518; 17/2:208–9, 306–7, 329; 19:149; 27:494; 29:44; 36:399–400; 34/2:496–97, 508; 37:625; 45:344; 46:228; 47:412; 49:191–95, 280–81, 660–64. Bugenhagen cited <i>Ein Kindelein so löblich</i> in two Epiphany sermons given on the same day (6 January 1525); see Georg Buchwald, ed., <i>Ungedruckte Predigten Johann Bugenhagens aus den Jahren 1524 bis 1529</i> (Leipzig: Heinsius, 1910), 70, 74.</p>
<p class="foot" id="rfn70"><a href="#/xhtml/#fn70">70</a>. WDKL 2:748 (no. 987).</p>
<p class="foot" id="rfn71"><a href="#/xhtml/#fn71">71</a>. WA TR 4, no. 4478; see also no. 4627.</p>
<p class="foot" id="rfn72"><a href="#/xhtml/#fn72">72</a>. Walther Lipphardt, “<i>Gelobet seist du Jesus Christ</i>: Zur Geschichte des Weinachtsliedes und seiner Melodie,” <i>Musik und Gottesdienst</i> 22 (1968): 151.</p>
<p class="foot" id="rfn73"><a href="#/xhtml/#fn73">73</a>. WDKL 2:703 (no. 910).</p>
<p class="foot" id="rfn74"><a href="#/xhtml/#fn74">74</a>. See Walther Lipphardt, “Ein Mainzer Prozessionale (um 1400) als Quelle deutsche geistliche Lieder,” JbLH 9 (1964): 95–121, esp. 116–21.</p>
<p class="foot" id="rfn75"><a href="#/xhtml/#fn75">75</a>. Lipphardt, “Ein Mainzer Prozessionale,” 102.</p>
<p class="foot" id="rfn76"><a href="#/xhtml/#fn76">76</a>. WA 35:172; AWA 4:91–95.</p>
<p class="foot" id="rfn77"><a href="#/xhtml/#fn77">77</a>. Zwickau manuscript, dating from around 1500; WA 35:173; AWA 4:91–95.</p>
<p class="foot" id="rfn78"><a href="#/xhtml/#fn78">78</a>. See the comparative example of the variants of the text and melody, AWA 4:91–95.</p>
<p class="foot" id="rfn79"><a href="#/xhtml/#fn79">79</a>. See Walther Lipphardt, “<i>Mitten wir im Leben sind</i>: Zur Geschichte des Liedes und seiner Weise,” JbLH 8 (1963): 99–118; see also Walther Lipphardt, “Die älteste Quelle des deutschen <i>Media vita</i>, eine Salzburger Handschrift vom Jahr 1456,” JbLH 11 (1966): 161–62.</p>
<p class="foot" id="rfn80"><a href="#/xhtml/#fn80">80</a>. Lipphardt, “<i>Mitten wir im Leben sind</i>,” 108.</p>
<p class="foot" id="rfn81"><a href="#/xhtml/#fn81">81</a>. Berlin Staatsbibliothek Ms. 40 021; AWA 4:82, which gives five variant forms in parallel columns.</p>
<p class="foot" id="rfn82"><a href="#/xhtml/#fn82">82</a>. See, for example, Kurt Henning, <i>Die geistliche Kontrafaktur in Jahrhundert der Reformation: Ein Beitrag zur Geschichte des deutschen Volks- und Kirchenliedes in XVI. Jahrhundert</i> (Halle: Niemayer, 1909; repr., Hildesheim: Olms, 1977), 9–31; and the “Catalogue of Songs” in Oettinger, <i>Music as Propaganda</i>, 213–402; see also the literature cited in n. 7 above.</p>
<p class="foot" id="rfn83"><a href="#/xhtml/#fn83">83</a>. WA TR 5, no. 6248 (uncertain date).</p>
<p class="foot" id="rfn84"><a href="#/xhtml/#fn84">84</a>. See, for example, chap. 2, “Luther as Musician,” in Robin A. Leaver, <i>Luther’s Liturgical Music: Principles and Implications</i> (Grand Rapids: Eerdmans, 2007), 21–64.</p>
<p class="foot" id="rfn85"><a href="#/xhtml/#fn85">85</a>. “Mir und dir ist niemand huld, das ist unser eigene Schuld.” WA TR 4, no. 4640 (1539); WA 38:338 (preface to a book by Urban Rhegius, 1535). Later “eigene” was replaced by “beide,” a typical modification in oral transmission: “Mir und dir ist niemand huld, das ist unser beide Schuld” (For me and thee there is no favor, that is both our fault); see Karl Friedrich Wilhelm Wander, <i>Deutsches Sprichtwörter-Lexicon: Ein Hausschatz für das deutsche Volk,</i> 5 vols. <i></i>(Leipzig: Brockhaus, 1873), 3:665.</p>
<p class="foot" id="rfn86"><a href="#/xhtml/#fn86">86</a>. WA TR 4, no. 5050.</p>
<p class="foot" id="rfn87"><a href="#/xhtml/#fn87">87</a>. “Anserinum Gänserich” [pseudonym of Karl Joseph Simrock], <i>Martinslieder hin und wieder In Deutschland gesungen Von Alten und von Jungen</i> (Bonn: [Marcus], 1846), 4–5. The song could be sung as a three-voice canon.</p>
<p class="foot" id="rfn88"><a href="#/xhtml/#fn88">88</a>. These songs did not begin to appear in print until 1531: <i>Etliche hubsche bergkreien geistlich und weltlich zu samen gebracht</i> (Zwickau: Meyerpeck, 1531); see Helmut Claus, <i>Die Zwickauer Drucke des 16. Jahrhunderts</i> (Gotha: Bebel, 1986), 191–92 (no. 192); and John Meier, ed., <i>Bergreihen: Ein Liederbuch des XVI. Jahrhunderts: Nach den vier ältesten Drucken von 1531, 1533, 1536 und 1537</i> (Halle: Niemeyer, 1892). The first item in <i>Das Weimarer Liederbuch: Schätzbare Sammlung alter Volkslieder</i>, ed. Konrad Kratzsch (Leipzig: Edition Leipzig, 1976; Zurich: Artemis, 1977)<i>, Bergkreyen: Etliche Schöne gesenge</i>, reprints many of the songs of the 1531 Zwickau collection. Some of the individual songs clearly postdate Luther’s Reformation, since they express specific Lutheran views, but others were clearly older.</p>
<p class="foot" id="rfn89"><a href="#/xhtml/#fn89">89</a>. See Martin Brecht, <i>Martin Luther: His Road to Reformation, 1483–1521</i>, trans. James L. Schaaf (Minneapolis: Fortress, 1985), 16.</p>
<p class="foot" id="rfn90"><a href="#/xhtml/#fn90">90</a>. Ulrike Hascher-Burger, <i>Gesungene Innigkeit: Studien zu einer Musikhandschrift der Devotio moderna (Utrecht, Universiteitsbibliotheek, ms. 16 H 34, olim B 113). Mit einer Edition der Gesänge,</i> Studies in the History of Christian Thought 106 (Leiden: Brill, 2002); and Ulrike Hascher-Burger, <i>Singen fur die Seligkeit: Studien zu einer Liedersammlung der Devotio moderna: Zwolle, Historisch Centrum, Overijssel, coll. Emmanuelshuizen, cat. VI. mit Edition und Faksimile,</i> Brill’s Series in Church History 28 (Leiden: Brill, 2007).</p>
<p class="foot" id="rfn91"><a href="#/xhtml/#fn91">91</a>. See further, Robin A. Leaver, “Luther as Composer,” <i>Lutheran Quarterly</i> 22 (2008): 389–90.</p>
<p class="foot" id="rfn92"><a href="#/xhtml/#fn92">92</a>. The following information is culled from Benzing, <i>Die Buchdrucker des 16. und 17. Jahrhunderts</i>, and registers the year the first printer set up a press: Augsburg (1468), Breslau (1475), Cologne (1464), Danzig (1498), Erfurt (1473), Frankfurt/Oder (1502), Freiburg (1491), Hamburg (1491), Heidelberg (1484), Leipzig (1481), Lübeck (1474), Magdeburg (1480), Mainz (ca. 1445), Münster (1485), Rostock (1476), Speyer (1471), Strasbourg (1460), Tübingen (1498), Ulm (1472), Worms (ca. 1504).</p>
<p class="foot" id="rfn93"><a href="#/xhtml/#fn93">93</a>. <i>Ji dissem buechlyn fyntman. L.xxv. hubscher lieder</i> (Cologne: Arnt von Aich, [ca. 1512]); DKL 1512<sup>02</sup>; <i>Das Liederbuch des Arnt von Aich (Köln um 1510)</i>, ed. Eduard Bernoulli and Hans Joachim Moser (Kassel: Bärenreiter, 1930).</p>
<p class="foot" id="rfn94"><a href="#/xhtml/#fn94">94</a>. <i>Aus sonder künstlicher art und mit höchstem fleiss seind diß gesangkbüecher</i> (Augsburg: Erhart Oeglin, 1512); DKL 1512<sup>03</sup>; <i>Erhart Oeglin’s Liederbuch zu vier Stimmen: Augsburg 1512</i>, ed. Robert Eitner and Julius Joseph Maier (Berlin: Trautwein, 1880; repr., New York: Broude, [1966]).</p>
<p class="foot" id="rfn95"><a href="#/xhtml/#fn95">95</a>. [<i>Liederbuch</i> (title page missing)] (Mainz: Peter Schöffer, 1513); DKL 1513<sup>02</sup>; <i>Peter Schöffers Liederbuch .</i> . . <i>nach dem einzigen bekannten Exemplar auf der Königl. Hof- und Staatsbibliothek zu München</i> (Munich: Münchener Bibliophilen, 1909).</p>
<p class="foot" id="rfn96"><a href="#/xhtml/#fn96">96</a>. Chrisman, <i>Bibliography of Strasbourg Imprints</i>, 190–91.</p>
<p class="foot" id="rfn97"><a href="#/xhtml/#fn97">97</a>. See Chrisman, <i>Bibliography of Strasbourg Imprints,</i> 189–90, 346–51.</p>
<p class="foot" id="rfn98"><a href="#/xhtml/#fn98">98</a>. Twenty-six printers were active between 1473 and 1530; see Benzing, <i>Die Buchdrucker des 16. und 17. Jahrhunderts</i>, 436–43.</p>
<p class="foot" id="rfn99"><a href="#/xhtml/#fn99">99</a>. William Young, “Music Printing in Sixteenth-Century Strasbourg,” <i>Renaissance Quarterly</i> 24 (1971): 486–501; see also Trocmé-Latter, <i>The Singing of the Strasbourg Protestants</i>, 54–65.</p>
<p class="foot" id="rfn100"><a href="#/xhtml/#fn100">100</a>. Koch, 1:399–405.</p>
<p class="foot" id="rfn101"><a href="#/xhtml/#fn101">101</a>. See the letter Luther wrote to the town of Esslingen, 11 October 1523; WA BR 3:167.</p>
<p class="foot" id="rfn102"><a href="#/xhtml/#fn102">102</a>. The Dorian melody, which circulated orally, is known only from one source: the tenor of the five-voice setting of <i>Lobt got, ir Christen allen</i> in <i>Hundert und fünfftzehen guter newen Liedlein mit vier, fünff, sechs stimmen . . . lustig zu singen</i> (Nuremberg: Ott, 1564), no. 5; see also Liliencron, <i>Die historischen Volkslieder</i>, vol. 4: <i>Nachtrag enthhaltend die Töne</i>, 36 (no. XV).</p>
<p class="foot" id="rfn103"><a href="#/xhtml/#fn103">103</a>. The allusion is to Revelation 14:6: “And I saw another angel fly in the midst of heaven, having the everlasting gospel to preach unto them that dwell on the earth” (KJV). This was the text of Bugenhagen’s sermon at Luther’s funeral in 1546. The German of the fourth line of the first stanza, “ganz luter offenbar” seems to be a pun on Luther’s name.</p>
<p class="foot" id="rfn104"><a href="#/xhtml/#fn104">104</a>. WDKL 3:74–77 (no. 107); the original thirty-two stanzas were later expanded to fifty-two.</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// chapter002.vue?64a05f8d